if (!customElements.get("vp-video-text-swap")) {
  class VpVideoTextSwap extends HTMLElement {
    constructor() {
      super();

      this._isPlaying = false;
      this._isMuted = false;
      this._onPlayClick = this._onPlayClick.bind(this);
      this._onPauseClick = this._onPauseClick.bind(this);
      this._onMuteClick = this._onMuteClick.bind(this);
      this._onVideoEnded = this._onVideoEnded.bind(this);
      this._onKeyDown = this._onKeyDown.bind(this);
      this._onPageShow = this._onPageShow.bind(this); // 👈 new
      this._onClickAnywhere = this._onClickAnywhere.bind(this);
    }

    connectedCallback() {
      this._observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            this._init();
            this._observer.disconnect();
          }
        },
        { threshold: 0.1 },
      );
      this._observer.observe(this);

      // 👇 Listen for bfcache restore
      window.addEventListener("pageshow", this._onPageShow);
    }

    disconnectedCallback() {
      this._observer?.disconnect();
      this._teardown();
      window.removeEventListener("pageshow", this._onPageShow); // 👈 cleanup
    }

    _init() {
      this._video = this.querySelector(".vp-vts__video");
      this._playBtn = this.querySelector('[data-vts-action="play"]');
      this._pauseBtn = this.querySelector('[data-vts-action="pause"]');
      this._muteBtn = this.querySelector('[data-vts-action="mute"]');
      this._controls = this.querySelector(".vp-vts__controls");

      if (!this._video) return;
      // Markup ships preload="none" when a poster covers the frame, so the clip costs nothing
      // at page load. Now that the section is in view, warm it up.
      if (this._video.preload === "none") this._video.preload = "metadata";
      this._video.load();
      this._video.muted = true;
      this._isMuted = true;

      this._attachEvents();
      this._syncAriaState();
    }

    // 👇 New: reset to initial state when restored from bfcache
    _onPageShow(event) {
      if (event.persisted) {
        this._resetToInitialState();
      }
    }

    // 👇 New: stop video and restore initial UI state
    _resetToInitialState() {
      if (this._video) {
        this._video.pause();
        this._video.currentTime = 0;
        this._video.muted = true;
      }
      this._isPlaying = false;
      this._isMuted = true;
      this.dataset.playing = false;
      delete this.dataset.started; // rewound to 0, so the poster is correct again

      this._syncAriaState();
      this._syncMuteButton();
    }

    _teardown() {
      this._playBtn?.removeEventListener("click", this._onPlayClick);
      this._pauseBtn?.removeEventListener("click", this._onPauseClick);
      this._muteBtn?.removeEventListener("click", this._onMuteClick);
      this._video?.removeEventListener("ended", this._onVideoEnded);
      this.removeEventListener("keydown", this._onKeyDown);
      this.removeEventListener("click", this._onClickAnywhere);
    }

    _attachEvents() {
      this._playBtn?.addEventListener("click", this._onPlayClick);
      this._pauseBtn?.addEventListener("click", this._onPauseClick);
      this._muteBtn?.addEventListener("click", this._onMuteClick);

      this._video.addEventListener("ended", this._onVideoEnded);
      this.addEventListener("keydown", this._onKeyDown);

      if (this.dataset.clickAnywhere === "true") {
        this.addEventListener("click", this._onClickAnywhere);
      }
    }

    _onClickAnywhere(event) {
      if (
        event.target.closest("button") ||
        event.target.closest("a") ||
        event.target.closest(".vp-vts__controls")
      ) {
        return;
      }
      
      if (!this._isPlaying) {
        this._onPlayClick();
      }
    }

    _onPlayClick() {
      if (!this._video) return;
      this._video.muted = false;
      this._isMuted = false;

      this._video
        .play()
        .then(() => {
          this._isMuted = this._video.muted;
          this._setPlayingState(true);
          this._syncMuteButton();
        })
        .catch(() => {
          this._video.muted = true;
          this._isMuted = true;
          this._video.play().then(() => {
            this._setPlayingState(true);
            this._syncMuteButton();
          });
        });
    }

    _onPauseClick() {
      if (!this._video) return;
      this._video.pause();
      this._video.currentTime = this._video.currentTime;
      this._setPlayingState(false);
    }

    _onMuteClick() {
      if (!this._video) return;

      this._video.muted = !this._video.muted;
      this._isMuted = this._video.muted;

      this._syncMuteButton();
    }

    _onVideoEnded() {
      this._setPlayingState(false);
    }

    _onKeyDown(event) {
      if (event.code === "Space" && document.activeElement === this) {
        event.preventDefault();
        if (this._isPlaying) {
          this._onPauseClick();
        } else {
          this._onPlayClick();
        }
      }
    }

    _setPlayingState(playing) {
      this._isPlaying = playing;
      this.dataset.playing = playing;
      // Latches on first play so pausing shows the paused frame, not the poster again.
      if (playing) this.dataset.started = "true";

      this._syncAriaState();
      this._syncMuteButton();

      this.dispatchEvent(
        new CustomEvent("vp:video:statechange", {
          bubbles: true,
          detail: { playing },
        }),
      );
    }

    _syncAriaState() {
      const primaryGroup = this.querySelector(".vp-vts__text-group--primary");
      const secondaryGroup = this.querySelector(".vp-vts__text-group--secondary");

      if (this._isPlaying) {
        if (secondaryGroup) primaryGroup?.setAttribute("aria-hidden", "true");
        secondaryGroup?.removeAttribute("aria-hidden");
        this._playBtn?.setAttribute("aria-hidden", "true");
        this._playBtn?.setAttribute("tabindex", "-1");
        this._controls?.setAttribute("aria-hidden", "false");
        // preventScroll: avoid stealing in-progress scroll (e.g. hash / nav spyscroll)
        // when this section initializes via IntersectionObserver mid-animation.
        this._pauseBtn?.focus({ preventScroll: true });
      } else {
        primaryGroup?.removeAttribute("aria-hidden");
        secondaryGroup?.setAttribute("aria-hidden", "true");
        this._playBtn?.removeAttribute("aria-hidden");
        this._playBtn?.removeAttribute("tabindex");
        this._controls?.setAttribute("aria-hidden", "true");
        this._playBtn?.focus({ preventScroll: true });
      }
    }

    _syncMuteButton() {
      if (!this._muteBtn) return;
      this._muteBtn.dataset.muted = this._isMuted;
      this._muteBtn.setAttribute(
        "aria-label",
        this._isMuted
          ? this._muteBtn.dataset.labelUnmute || "Unmute video"
          : this._muteBtn.dataset.labelMute || "Mute video",
      );
    }
  }

  customElements.define("vp-video-text-swap", VpVideoTextSwap);
}
